// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';

import { PaymentCardFormContext } from '../contexts';
import {
  FormContextType,
  TokenFormFieldConfig,
  TokenFormProps,
} from '../@types';
import { DefaultTheme } from '../theme/default';
import { createLogLocal } from '../utils/createLogLocal';
import { deepMerge } from '../utils/deepMerge';

import { PaymentCardForm as PaymentCardFormComponent } from './PaymentCardForm';
// import { CSSReset } from './CSSReset';

const log = createLogLocal('CardTokenForm.tsx');

export const Events = {
  LoadStart: 'on-load-start',
  LoadEnd: 'on-load-end',
  Change: 'on-change',
  Submit: 'on-submit',
  Validate: 'on-validate',
  Tokenize: 'on-tokenize',
  Error: 'on-error',
  Hello: 'on-hello',
};

const ApiHosts = {
  local: {
    host: 'https://api.cloudbeds-local.com',
    origin: window.location.origin,
  },
  dev: {
    host: 'https://api.us2.cloudbeds-stage.com',
    origin: 'https://us2.cloudbeds-stage.com',
  },
  stage: {
    host: 'https://api.us1.cloudbeds-stage.com',
    origin: 'https://hotels.cloudbeds-stage.com',
  },
  prod: {
    // host: 'https://api.cloudbeds.com',
    host: 'https://api.us1.cloudbeds.com',
    origin: 'https://hotels.cloudbeds.com',
  },
  'prod-us2': {
    host: 'https://api.us2.cloudbeds.com',
    origin: 'https://us2.cloudbeds.com',
  },
};

export const defaultFormConfig: TokenFormFieldConfig = {
  cardFields: {
    cardholderName: {
      required: true,
    },
    cardNumber: {
      required: true,
    },
    expiry: {
      required: true,
    },
    cvv: {
      required: true,
    },
  },
  // contactFields: {
  //   firstName: {
  //     required: true,
  //   },
  //   lastName: {
  //     required: true,
  //   },
  // },
  // billingFields: {
  //   billingDocumentType: {
  //     required: true,
  //   },
  //   billingDocumentNumber: {
  //     required: true,
  //   },
  //   billingAddress1: {
  //     required: true,
  //   },
  //   billingAddress2: {
  //     required: false,
  //   },
  //   billingCountry: {
  //     required: true,
  //   },
  //   billingState: {
  //     required: false,
  //   },
  //   billingEmail: {
  //     required: false,
  //   },
  // },
};

const scopedCss = true; // TODO: shold it be moved into .env or prop?

const dispatchEvent = (event: string, data: any) => {
  window.dispatchEvent(
    new CustomEvent(event, {
      // composed: true,
      bubbles: true,
      detail: {
        data,
      },
    }),
  );
};

// eslint-disable-next-line import/no-mutable-exports, @typescript-eslint/no-unused-vars
export let submit = (callback?: () => unknown) => {
  log('[@cloudbeds/payment-tokenization] form submitted.');
  callback?.();
};
// eslint-disable-next-line import/no-mutable-exports, @typescript-eslint/no-unused-vars
export let validate = (callback?: () => unknown) => {
  log('[@cloudbeds/payment-tokenization] form validated.');
  callback?.();
};
// eslint-disable-next-line import/no-mutable-exports, @typescript-eslint/no-unused-vars
export let sayHello = () => {
  log('[@cloudbeds/payment-tokenization] hello.');
};

export const CardTokenForm = forwardRef(
  ({ lang = 'en', mode = 'test', ...props }: TokenFormProps, fwdRef: any) => {
    const ref = useRef();
    const [config, setConfig] = useState<FormContextType | null>(null);
    const apiTokenConfig = {
      apiEnv: props.env || 'local',
      apiToken: props.apiToken || '',
    };

    let submitCallback = props?.onSubmit;
    let validateCallback = props?.onValidate;

    /**
     * This couner is to re-request auth key before it expires,
     * added as dependency to the useEffect that loads auth key from backend.
     */
    const [expirationCounter, setExpirationCounter] = useState(0);
    useEffect(() => {
      const interval = setInterval(
        () => {
          if (config) {
            setConfig(null);
          }
          setExpirationCounter(c => c + 1);
        },
        15 * 60 * 1000,
      );
      return () => clearInterval(interval);
    }, [expirationCounter, config]);

    useEffect(() => {
      if (config) return;
      log('[@cloudbeds/payment-tokenization] Tokenex config reloaded');
      const apiHost = ApiHosts[apiTokenConfig.apiEnv].host;
      const authKeyUrl = `${apiHost}/payments-configuration/v1.0/tokenex/authentication_key`;
      fetch(authKeyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          Authorization: `Bearer ${apiTokenConfig.apiToken}`,
        },
        body: JSON.stringify({
          // origin: window.location.origin,
          origins: [ApiHosts[apiTokenConfig.apiEnv].origin],
          mode: mode.toUpperCase(),
        }),
      })
        .then(response => response.json())
        .then(data => {
          const {
            authentication_key: authKeys,
            client_id: tokenExID,
            timestamp,
          } = data;

          type TokenexConfigType = Record<
            'cvvAuthKey' | 'cvvScheme' | 'cardAuthKey' | 'cardScheme',
            string
          >;
          const tokenexConfig = (
            authKeys as Record<'authentication_key' | 'scheme', string>[]
          ).reduce<TokenexConfigType>((a, b) => {
            return {
              ...a,
              ...(b.scheme.includes('GUID') && {
                cvvAuthKey: b.authentication_key,
                cvvScheme: b.scheme,
              }),
              ...(!b.scheme.includes('GUID') && {
                cardAuthKey: b.authentication_key,
                cardScheme: b.scheme,
              }),
            };
          }, {} as TokenexConfigType);

          setConfig({
            tokenexConfig: {
              card: {
                origin: ApiHosts[apiTokenConfig.apiEnv].origin,
                timestamp,
                tokenExID,
                tokenScheme: tokenexConfig.cardScheme,
                authenticationKey: tokenexConfig.cardAuthKey,
              },
              cvv: {
                origin: ApiHosts[apiTokenConfig.apiEnv].origin,
                timestamp,
                tokenExID,
                tokenScheme: tokenexConfig.cvvScheme,
                authenticationKey: tokenexConfig.cvvAuthKey,
              },
            },
            formConfig: deepMerge({ ...defaultFormConfig }, props.fields || {}),
          });
        })
        .catch(error => {
          const errorData = {
            loaded: false,
            message:
              error.message || 'NetworkError: Failed to load token form.',
          };
          props.onError?.(errorData);
          dispatchEvent(Events.Error, errorData);
        });
    }, [props, config, apiTokenConfig.apiEnv, apiTokenConfig.apiToken, mode]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const theme = useMemo(
      () => extendTheme(DefaultTheme, props.theme || {}),
      [props.theme],
    );
    // const theme = useMemo(() => extendTheme({ ...DefaultTheme }), []);
    // log(
    //   'theme',
    //   theme,
    //   DefaultTheme,
    //   props.theme,
    //   deepMerge({}, DefaultTheme, props.theme || {}),
    // );

    const validateInternalForm = async () => {
      const validateFunc = (ref.current as any)?.validate;
      if (validateFunc) return validateFunc();
      return true;
    };

    const getInternalFormState = () => {
      const getStateFunc = (ref.current as any)?.getFormState;
      if (getStateFunc) return getStateFunc();
      return null;
    };

    const submitInternalForm = async () => {
      const isValid = await validateInternalForm();
      log('[@cloudbeds/payment-tokenization] Validate on submit:', isValid);
      if (isValid) {
        const submitFunc = (ref.current as any)?.submit;
        if (submitFunc) return submitFunc();
        return true;
      }
      log('[@cloudbeds/payment-tokenization] Form is not valid.');
      return false;
    };

    sayHello = () => {
      log('[@cloudbeds/payment-tokenization] Hello!');
    };

    submit = async callbackFn => {
      log('[@cloudbeds/payment-tokenization] external submit call.');
      /* we overwrite default submit handler (extracted from props) */
      if (callbackFn) submitCallback = callbackFn;
      return submitInternalForm();
    };

    validate = async callbackFn => {
      log('[@cloudbeds/payment-tokenization] external validation call.');
      /* we overwrite default validate handler (extracted from props) */
      if (callbackFn) validateCallback = callbackFn;
      const isValid = await validateInternalForm();
      return {
        valid: isValid,
        ...(!isValid && { errors: getInternalFormState()?.errors }),
      };
    };

    const onLoadStart = () => {
      props.onLoadStart?.();

      dispatchEvent(Events.LoadStart, {});
    };

    const onLoadEnd = () => {
      props.onLoadEnd?.();

      dispatchEvent(Events.LoadEnd, {});
    };

    const onFormSubmit = (data: any) => {
      submitCallback?.(data);

      dispatchEvent(Events.Submit, data);
    };

    const onFormTokenize = (data: any) => {
      props.onTokenize?.(data);

      dispatchEvent(Events.Tokenize, data);
    };

    const onFormChange = (data: any) => {
      log(
        '[@cloudbeds/payment-tokenization] Change event triggered by component.',
      );
      props.onChange?.(data);

      dispatchEvent(Events.Change, data);
    };

    const onFormError = (data: any) => {
      window.console.error(data);
      props.onError?.(data);

      dispatchEvent(Events.Error, data);
    };

    const onFormValidate = (data: any) => {
      window.console.error(data);
      validateCallback?.(data);

      dispatchEvent(Events.Validate, data);
    };

    // useEffect(() => {
    //   log('App Tokenex Config', config);
    // }, [config]);

    useImperativeHandle(fwdRef, () => ({
      hello: () => {
        log('[@cloudbeds/payment-tokenization] Hello');
      },
      submit: submitInternalForm,
      validate: validateInternalForm,
    }));

    return (
      <ChakraProvider theme={theme} resetCSS={!scopedCss}>
        {/* {scopedCss && <CSSReset scope=".token-form-scope" />} */}
        <div className="token-form-scope">
          <PaymentCardFormContext.Provider value={config}>
            <PaymentCardFormComponent
              validateOnBlur={true}
              ref={ref}
              lang={lang}
              mode={mode}
              labels={props.labels}
              canEditGuest={props.canEditGuest}
              defaultValues={props.defaultValues}
              onSubmitHandler={onFormSubmit}
              onErrorHandler={onFormError}
              onChangeHandler={onFormChange}
              onValidateHandler={onFormValidate}
              onTokenizeHandler={onFormTokenize}
              onLoadStart={onLoadStart}
              onLoadEnd={onLoadEnd}
            />
          </PaymentCardFormContext.Provider>
        </div>
      </ChakraProvider>
    );
  },
);
